// // import { jwtDecode } from "jwt-decode";
// import React, { useEffect } from "react";
// import { Navigate } from "react-router-dom";

// const secret = "autodocz";

// const ProtectedRoute = ({ children }: any) => {
//   const token = localStorage.getItem("token");
//   return token ? children : <Navigate to="/login" />;
// };

// export default ProtectedRoute;


import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { path } from "../navigation/path";

interface ProtectedRouteProps {
  allowedRoles?: number[]; // Legacy: for Super Admin (0) and other numeric roles
  requiredPermission?: string | string[]; // New: specific permission(s) required
  excludeMemberBasedRoles?: boolean; // Exclude users who only have member.* permissions (for Admin User Management)
}

const ProtectedRoute = ({ allowedRoles, requiredPermission, excludeMemberBasedRoles }: ProtectedRouteProps) => {
  const token = localStorage.getItem("token");
  const role = Number(localStorage.getItem("role"));
  const isSuperAdminRole = role === 0;
  
  // Get permissions from localStorage
  const permissionsStr = localStorage.getItem("permissions");
  let permissions: string[] = [];
  try {
    permissions = permissionsStr ? JSON.parse(permissionsStr) : [];
  } catch (e) {
    console.error("Error parsing permissions:", e);
    permissions = [];
  }
  
  // Users with "admin" permission have all permissions like Super Admin (but are NOT Super Admin)
  const hasAllPermissions = permissions.includes("admin");
  // isSuperAdmin should only be true for actual Super Admin (role = 0), not for admin permission users
  const isSuperAdmin = isSuperAdminRole;

  if (!token) {
    return <Navigate to={path.LOGIN} replace />;
  }

  // SUPER ADMIN EXCEPTION: Has unrestricted access
  // Also users with "admin" permission have unrestricted access (but are NOT Super Admin)
  if (isSuperAdmin || hasAllPermissions) {
    return <Outlet />;
  }

  // Special check: Exclude member-based roles from Admin User Management
  if (excludeMemberBasedRoles) {
    const hasUserManage = permissions.includes("user.manage");
    const hasMemberManage = permissions.includes("member.manage");
    
    // If user only has member.manage but not user.manage, deny access
    if (hasMemberManage && !hasUserManage) {
      return <Navigate to="/" replace />;
    }
  }

  // Check if user has required permission
  if (requiredPermission) {
    // Handle both string and array of permissions
    const requiredPerms = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    
    const hasPermission = requiredPerms.some(perm => {
      // Direct permission match
      if (permissions.includes(perm)) return true;
      
      // Check for "admin" permission (users with admin permission have all permissions)
      if (permissions.includes("admin")) return true;
      
      // For user.manage with excludeMemberBasedRoles, only allow user.manage specifically
      if (excludeMemberBasedRoles && perm === "user.manage") {
        // Only allow if user has user.manage specifically, not member.manage
        return permissions.includes("user.manage");
      }
      
      // Check for .manage permission (e.g., zone.manage includes zone.create, zone.update, zone.delete)
      // But skip member/user equivalence when excludeMemberBasedRoles is true
      const basePermission = perm.split('.')[0];
      if (excludeMemberBasedRoles && (basePermission === "user" || basePermission === "member")) {
        // For user.* permissions, only check user.* permissions, not member.*
        if (basePermission === "user") {
          return permissions.includes("user.manage");
        }
        // For member.* permissions in this context, don't allow (shouldn't happen for user.manage)
        return false;
      }
      
      if (permissions.includes(`${basePermission}.manage`)) {
        return true;
      }
      
      return false;
    });
    
    if (!hasPermission) {
      // No-role users with member_id: send to My Information instead of dashboard
      const memberId = localStorage.getItem("member_id");
      const isDashboardRequired = requiredPerms.some(p => p === "dashboard.view");
      if (isDashboardRequired && memberId) {
        return <Navigate to={path.MY_PROFILE} replace />;
      }
      return <Navigate to="/" replace />;
    }
  }

  // Legacy: Check numeric roles (for backward compatibility)
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to="/" replace />; 
  }

  return <Outlet />;
};

export default ProtectedRoute;
